/**
 * Provides an optionally-modifiable, `Field`-based table UI for displaying model attributes.
 * ## Fields
 * Name | Description
 * --- | ---
 * collection | The `Backbone.Collection` of models in the table.
 * rows | The array of `ModelRowView` objects in the table.
 * columns | The array of `ColumnHeaderView` objects in the table.
 * fields | The array of `Field` models representing the fields displayed by the table.
 * pager | If `options.paginate` is not false, a PagerView control.
 *
 * ## Events
 * Name | Arguments | Description
 * row-add | The `ModelRowView` that was added. | Fired whenever a row is added to the table.
 * row-remove | The `ModelRowView` that was removed. | Fired whenever a row is removed from the table.
 * row-commit | The `ModelRowView` on which the commit occurred. | Fires whenever the `commit` event of a displayed row is fired.
 * row-delete | The `ModelRowView` on which the delete occurred. | Fires whenever the `remove` event of a displayed row is fired.
 * @module app/views/CollectionTableView
 */
import Backbone from "backbone";
import _ from "underscore";
import $ from "jquery";
import ModelRowView from "core/views/ModelRowView";
import ColumnHeaderView from "core/views/ColumnHeaderView";
import PagerView from "core/views/PagerView";
var DEFAULT_PAGE_SIZE = 100;

export default Backbone.View.extend({
  tagName: "table",

  className: "table",

  collection: [],
  rows: [],
  columns: [],
  fields: [],

  template: _.template(
    '<tr class="header-row"><%if(allowSave){%><th class="anchor save-anchor"></th><%}%><%if(allowDelete){%><th class="anchor delete-anchor"></th><%}%></tr>'
  ),

  /**
   * Initializes the control.
   * @param  {object} options The options hash.
   * @param {Backbone.Collection|Array} options.collection The set of models to display in the table.
   * @param {Array} options.fields The array of `Field` models to present in the table.
   * @param {Boolean} options.allowSave True to display a UI for saving modified rows, otherwise false.
   * @param {Boolean} options.allowDelete True to display a UI for deleting rows, otherwise false.
   * @param {Boolean} options.autoSave True to automatically persist model changes, otherwise false. Default is false.
   * @param {string} options.nameBase **Deprecated** A prefix for each input tag's "name" attribute in each row.
   * @param {Number|Boolean} options.paginate Indicates whether or not pagination should be used. If true, pages will contain 100 rows, and if a number, pages will contain that number of rows. If false, rows will not be paginated.
   * @param {app/Context} options.context A context to be used by this `CollectionTableView`.
   * @param {string} [options.mode] Specifies the display mode for the table. This should be "edit", "view". If not provided, each row will enter "edit" mode upon cursor entry.
   *
   */
  initialize: function (options) {
    this.options = _.defaults({}, options, {
      allowSave: true,
      allowDelete: true,
      autoSave: false,
      paginate: false,
    });

    this.collection = this.options.collection || [];

    if (_.isArray(this.collection)) {
      this.collection = new Backbone.Collection(this.collection);
    }

    this._resetModels(true);

    if (this.options.paginate) {
      this.pager = new PagerView({
        count: this.getPageCount(),
      });
      this.pager.render();
      this.pager.on(
        "select",
        function (current) {
          var index = current - 1;

          this.showPage(index);
        },
        this
      );
    }

    this.rows = [];
    this.columns = [];
    this.fields = [];

    this._suppliedFields = this.options.fields || [];
  },

  render: function () {
    this._clearFields();
    this.$el.html(this.template(this.options));

    var inserter = this._getInserter();

    _.each(
      this._suppliedFields,
      function (item) {
        this._addField(item, inserter);
      },
      this
    );

    var addModel = _.bind(this._addModel, this);
    var removeModel = _.bind(this._removeModel, this);

    //this._collectionIterator(addModel, this);
    this.collection.on("add", addModel);
    this.collection.on("remove", removeModel);
    this.refreshCurrentPage();
  },

  /**
   * Given a model, determines whether or not it passes the currently-applied filter(s).
   * @private
   * @param  {Backbone.Model} model The model.
   * @return {Boolean}       True if the model passes the filter(s) (or if there is no filter), otherwise false.
   */
  _passesFilter: function (model) {
    var result = true;

    if (this._filter) {
      _.each(this._filter, function (filterFn) {
        result = result && filterFn(model);
      });
    }

    return result;
  },

  /**
   * Creates a row for the given model. The row will be rendered but will not be added to the DOM or the `rows` collection.
   * @private
   * @param  {Backbone.Model} model The model to associate with the row.
   * @return {ModelRowView}       The row created.
   */
  _createRow: function (model) {
    var view = new ModelRowView({
      model: model,
      fields: this.fields,
      allowSave: this.options.allowSave,
      allowDelete: this.options.allowDelete,
      autoSave: this.options.autoSave,
      mode: this.options.mode,
    });
    var oldRemove = view.remove;

    view.remove = _.bind(function () {
      var idx = this.rows.indexOf(view);
      oldRemove.call(view);
      view.off("commit");
      view.off("remove");

      if (idx !== -1) {
        this.rows.splice(idx, 1);
      }
    }, this);

    view.on(
      "commit",
      function () {
        this.trigger("row-commit", view);
      },
      this
    );

    view.on(
      "remove",
      function () {
        this.trigger("row-delete", view);
      },
      this
    );

    view.render();

    this.rows.push(view);

    return view;
  },

  _addModel: function (model) {
    if (this._passesFilter(model)) {
      this._filteredModels.push(model);
    }

    this.refreshCurrentPage();
  },

  /**
   * Add a model to the table and its underlying collection.
   * @param {mixed} model The model to add.
   */
  addModel: function (model) {
    this.collection.add(model);
  },

  /**
   * Remove a model from the table and its underlying collection.
   * @param  {mixed} model The model to remove.
   */
  removeModel: function (model) {
    this.collection.remove(model);
  },

  _removeModel: function (model) {
    var idx = this._filteredModels.indexOf(model);

    if (idx !== -1) {
      this._filteredModels.splice(idx, 1);
    }

    this.refreshCurrentPage();
  },

  /**
   * Refreshes the data on the current page. If pagination is not enabled, the table as a whole will be refreshed.
   */
  refreshCurrentPage: function () {
    if (this.options.paginate) {
      this.pager.setCount(this.getPageCount());

      if (!_.isEmpty(this._filter)) {
        if (this.pager.current > this.getPageCount()) {
          this.showPage(0); // go to the first page
        } else {
          this.showPage(this.getPageCount() - 1); //go to the last page
        }
      } else {
        this.showPage(this.pager.current - 1);
      }
    } else {
      this.showPage(0);
    }
  },

  /**
   * Reloads the `_filteredModels` collection, taking filters and sorts into account.
   * @param  {Boolean} copy True to copy models over from `collection` field, otherwise false. If false, `_filteredModels` will simply be resorted.
   * @return {Array} Returns `_filteredModels`.
   */
  _resetModels: function (copy) {
    if (copy) {
      this._filteredModels = [];

      _.each(
        this.collection.models,
        function (model) {
          if (this._passesFilter(model)) {
            this._filteredModels.push(model);
          }
        },
        this
      );
    }

    if (_.isFunction(this._comparator)) {
      this._filteredModels.sort(this._comparator);
    }

    return this._filteredModels;
  },

  /**
   * Displays the rows for the given page.
   * @param  {Integer} pageIndex The page to show.
   */
  showPage: function (pageIndex) {
    var rowIndex;
    var pageStart = this.getPageStart(pageIndex),
      pageLength = this.getPageLength(pageIndex);
    var modelList = this._resetModels();
    var row;

    _.each(this.rows.slice(), function (row) {
      row.remove();
    });

    for (rowIndex = pageStart; rowIndex < pageStart + pageLength; rowIndex++) {
      row = this._createRow(modelList[rowIndex]);
      this.$el.append(row.$el);
    }
  },

  /**
   * Gets the starting index of models to display on a page.
   * @param  {Integer} pageIndex The index of the page.
   * @return {Number}           The number of records on the page.
   */
  getPageStart: function (pageIndex) {
    return pageIndex * this.getPageSize();
  },

  /**
   * Gets the actual number of records on a page.
   * @param  {Integer} pageIndex The index of the page.
   * @return {Number}           The number of records on the page.
   */
  getPageLength: function (pageIndex) {
    if (pageIndex == this.getPageCount() - 1) {
      return this._filteredModels.length - pageIndex * this.getPageSize();
    } else {
      return this.getPageSize();
    }
  },

  /**
   * Gets the maximum number of records which will be displayed in each page.
   * @return {Number} The maximum number of records for a page.
   */
  getPageSize: function () {
    if (this.options.paginate) {
      if (_.isNumber(this.options.paginate)) {
        return this.options.paginate;
      } else {
        return DEFAULT_PAGE_SIZE;
      }
    } else {
      return this._filteredModels.length;
    }
  },

  /**
   * Gets the total of number of pages in the table.
   * @return {Number} The number of pages of records in the table.
   */
  getPageCount: function () {
    return Math.ceil(this._filteredModels.length / this.getPageSize());
  },

  _doSort: function (sortColumn, sortField, sortType) {
    if (sortType) {
      var getSortValue = function (model, field) {
        return field.getSortValue(model);
      };
      var modifier = sortType == ColumnHeaderView.SortAscending ? 1 : -1;

      this._comparator = function (first, second) {
        var firstValue = getSortValue(first, sortField);
        var secondValue = getSortValue(second, sortField);
        var result = 0;

        if (firstValue && secondValue) {
          if (firstValue < secondValue) {
            result = -1;
          } else if (firstValue > secondValue) {
            result = 1;
          }
        } else if (firstValue) {
          result = -1;
        } else if (secondValue) {
          result = 1;
        }

        result = result * modifier;

        return result;
      };
      this._sortColumn = sortColumn;
      this.refreshCurrentPage();
    } else {
      this.clearSort();
    }
  },

  _doFilter: function (filterField, filterValue) {
    var filter = (this._filter = this._filter || {}); //set filter to this._filter, which is first set to either itself or a default value if it is null
    var filterFn = function () {
      return true;
    };
    var attribute;

    if (_.isString(filterField)) {
      _.each(this.fields, function (field) {
        if (field.attributes.name == filterField) {
          attribute = field;
        }
      });
    } else {
      attribute = filterField;
    }

    if (attribute) {
      if (filterValue) {
        if (_.isFunction(filterValue)) {
          filterFn = filterValue;
        } else {
          filterFn = function (model) {
            return (
              String(attribute.getFilterValue(model)).indexOf(filterValue) != -1
            );
          };
        }

        filter[attribute.id] = filterFn;
      } else {
        delete filter[attribute.id];
      }
    }

    this._resetModels(true);
    this.refreshCurrentPage();
  },

  /**
   * Clear the sort mode for the currently-sorted column.
   */
  clearSort: function () {
    if (this._sortColumn) {
      this._sortColumn.clearSort();
      this._sortColumn = null;
      this._comparator = null;
      this._resetModels(true);
    }

    this.refreshCurrentPage();
  },

  /**
   * Clear the filter value for all filtered columns.
   */
  clearFilter: function () {
    _.each(this.columns, function (columnHeader) {
      columnHeader.clearFilter();
    });
    this._filter = {};
    this._resetModels(true);
    this.refreshCurrentPage();
  },

  /**
   * Set the sort mode for the given field.
   * @param {string} sortField The id of the field on which to sort. The field must be a member of the `fields` property.
   * @param {string} sortType  One of the sort constants from `ColumnHeaderView`.
   */
  setSort: function (sortField, sortType) {
    var sortColumn;

    this.clearSort();
    _.each(this.columns, function (column) {
      if (column.field.attributes.name == sortField) {
        sortColumn = column;
      }
    });

    if (sortColumn) {
      sortColumn.setSort(sortType);
    }
  },

  /**
   * Sets a filter on the given field.
   * @param {string|Field} filterField The id of a field in `fields` on which to filter *or* an instance of `Field` on which to filter.
   * @param {mixed} filterValue The filter value to use.
   */
  setFilter: function (filterField, filterValue) {
    var filterColumn;

    _.each(this.columns, function (column) {
      if (
        column.field.attributes.name == filterField &&
        column.field.attributes.filterable
      ) {
        filterColumn = column;
      }
    });

    if (filterColumn) {
      filterColumn.setValue(filterValue);
    } else {
      this._doFilter(filterField, filterValue);
    }
  },

  /**
   * Hides the row at the given index.
   * @param  {Integer} row The index of the row to hide.
   */
  hide: function (row) {
    this.rows[row].$el.hide();
  },

  /**
   * Shows the row at the given index.
   * @param  {Integer} row The index of the row to show.
   */
  show: function (row) {
    this.rows[row].$el.show();
  },

  _addField: function (field, inserter) {
    var columnHeader = new ColumnHeaderView({
      field: field,
    });

    columnHeader.on(
      "sort",
      function (type) {
        this._doSort(columnHeader, field, type);
      },
      this
    );
    columnHeader.on(
      "filter",
      function (value) {
        this._doFilter(field, value);
      },
      this
    );
    this.columns.push(columnHeader);
    columnHeader.render();
    inserter(columnHeader.$el);

    _.each(this.rows, function (row) {
      row.addField(field);
    });
    this.fields.push(field);
  },

  _clearFields: function () {
    _.each(this.columns, function (columnHeader) {
      columnHeader.off();
    });

    this.fields = [];
  },

  _getInserter: function () {
    var inserter = function () {};
    var anchor;

    if (this.options.allowSave || this.options.allowDelete) {
      anchor = this.$(".anchor").first();

      inserter = _.bind(anchor.before, anchor);
    } else {
      anchor = this.$(".header-row");

      inserter = _.bind(anchor.append, anchor);
    }

    return inserter;
  },

  /**
   * Adds the given field to the table.
   * @param {Field} field The field to add.
   */
  addField: function (field) {
    this._suppliedFields.push(field);
    this._addField(field, this._getInserter());
  },

  addFields: function (fields) {
    _.each(fields, this.addField, this);
  },

  /**
   * Removes the field with the given id from the table.
   * @param  {string} attribute The id of the field to remove.
   */
  removeField: function (attribute) {
    _.each(this.rows, function (row) {
      row.removeField(attribute);
    });
    var removeIndex = false;

    _.each(this._suppliedFields, function (field, idx) {
      if (field.attributes.name == attribute) {
        removeIndex = idx;
      }
    });

    if (removeIndex !== false) {
      this._suppliedFields.splice(removeIndex, 1);

      if (removeIndex < this.fields.length) {
        this.fields.splice(removeIndex, 1);
        this.columns.splice(removeIndex, 1).remove();
      }
    }
  },

  delegateEvents: function () {
    Backbone.View.prototype.delegateEvents.apply(this, arguments);
    _.each(this.rows, function (row) {
      row.delegateEvents();
    });
    _.each(this.columns, function (column) {
      column.delegateEvents();
    });
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/core/views/CollectionTableView.js
// module id = 29
// module chunks = 1